import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

/**
 * All business days are Asia/Colombo, matching the backend. A browser in
 * another timezone must still agree with the server about which day a
 * collection belongs to.
 */
export const APP_TZ = 'Asia/Colombo';

export type IsoDate = string; // YYYY-MM-DD

export function today(): IsoDate {
  return dayjs().tz(APP_TZ).format('YYYY-MM-DD');
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return dayjs(date, 'YYYY-MM-DD').add(days, 'day').format('YYYY-MM-DD');
}

export function startOfMonth(date: IsoDate = today()): IsoDate {
  return dayjs(date, 'YYYY-MM-DD').startOf('month').format('YYYY-MM-DD');
}

/** "13 Jul 2026" — unambiguous, unlike any all-numeric format. */
export function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : '—';
}

/** "2026/07/13", the format written on the printed report header. */
export function formatReportDate(date: string): string {
  const parsed = dayjs(date, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.format('YYYY/MM/DD') : date;
}

/** "7/16", the short form used in the report's Cheque Date column. */
export function formatShortDate(date: string | null): string {
  if (!date) return '';
  const parsed = dayjs(date, 'YYYY-MM-DD');
  return parsed.isValid() ? `${parsed.month() + 1}/${parsed.date()}` : '';
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = dayjs(value).tz(APP_TZ);
  return parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm') : '—';
}

/** "3 days ago" — for how long an invoice has been overdue. */
export function fromNow(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.fromNow() : '—';
}

export function daysOverdue(dueDate: string): number {
  return Math.max(0, dayjs(today(), 'YYYY-MM-DD').diff(dayjs(dueDate, 'YYYY-MM-DD'), 'day'));
}

export function isValidIsoDate(value: string): boolean {
  return dayjs(value, 'YYYY-MM-DD', true).isValid();
}

/** Chart axis labels: "13 Jul". */
export function formatAxisDate(date: string): string {
  const parsed = dayjs(date, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.format('DD MMM') : date;
}

export { dayjs };
